import { useSelector } from 'react-redux';
import { RootState } from 'src/shared/state/root-reducer';
import { Button } from '@finpay-development/shared-components';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import '../../../scss/components/_confirmation-modal.scss';
import WarningIcon from '@mui/icons-material/Warning';
import { LaunchDataChange, LaunchChangeDataSet } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/models/patient';

interface confirmChangesModalOpenProps {
  open: boolean;
  handleModalCancel: () => void;
  handleSave: () => void;
  changes: LaunchDataChange
}

type PayerChangeWithPtRecordId = LaunchChangeDataSet & {ptRecordId?: number};

export function ConfirmChangesModal(props:confirmChangesModalOpenProps) {
  const { open, handleModalCancel, handleSave, changes } = props;
  const{facilityChange, payerChange, clientId } = changes
  const hasPayerChanged = payerChange.hasChanged;
  const hasFacilityChanged = facilityChange.hasChanged;
  const ptRecordId = (payerChange as PayerChangeWithPtRecordId)?.ptRecordId;
  const shouldShowUpdatedPayer =
      payerChange?.hasChanged &&
      !!payerChange?.newName &&
      (ptRecordId != null
          ? payerChange.newId !== ptRecordId
          : payerChange.newName !== payerChange.ptRecordName);

  const noPayerExists = payerChange.hasChanged && !payerChange?.newName;
  const ChangeItem = ({ label, value }: { label: string; value: string }) => (
    <Typography variant="body1" style={{paddingTop: '6px'}}>
      <strong>{label}:</strong> {value}
    </Typography>
  );
  
  const allClients = useSelector((state: RootState) => {
      return state.implementationContext?.implementationSpecialistClient.allClients;
  });

  const matchedClient = allClients.find(client => client.clientId === Number(clientId));

  const matchedCurrentFacility = matchedClient?.clientFacilities
      ?.find(facility => facility.facilityId === facilityChange.prevId);

  const matchedNewFacility = matchedClient?.clientFacilities
      ?.find(facility => facility.facilityId === facilityChange.newId);

  const payerChangeOnly = (
    <div test-id="payer-discrepancy">
      <ChangeItem label="Current Payer" value={payerChange.prevName} />
      <ChangeItem label="Updated Payer" value={payerChange.newName ?? "None"} />
    </div>
  );

  const fAndPChanges = (
    <div test-id="fAndPChanges">
      <div>
        <ChangeItem label="Current Facility" value={matchedCurrentFacility?.facilityName!} />
        <ChangeItem label="Updated Facility" value={matchedNewFacility?.facilityName!} />
      </div>
      <div style={{paddingTop: "16px"}}>
        <ChangeItem label="Current Payer" value={payerChange.prevName} />
        <ChangeItem label="Updated Payer" value={payerChange.newName ?? "None"} />
      </div>
    </div>
  );

  const facilityChangeOnly = (
    <div test-id="facility-discrepancy">
      <ChangeItem label="Current Facility" value={matchedCurrentFacility?.facilityName!} />
      <ChangeItem label="Updated Facility" value={matchedNewFacility?.facilityName!} />
    </div>
  )

  return (
      <Dialog
          scroll="paper"
          className="modal user-modal requirements-modal"
          open={open}
          aria-labelledby="customized-dialog-title"
          maxWidth="md"
          test-id="detect-discrepancy"
      >
        <DialogTitle className="spa-modal-title">
          We noticed some changes!
        </DialogTitle>
        <DialogContent dividers>
          <Grid container direction="row" style={{ padding: "1.5rem 0 2rem 0", display: "flex", flexDirection: "column" }}>
            <Typography className="pt-2" style={{ paddingTop: "0", fontSize: "16px", paddingBottom: "20px", fontFamily: "Inter", lineHeight: "25px", fontWeight: "400", color: "#808080" }}>
              Do you want to update?
            </Typography>

            <Typography className="pt-2" style={{ paddingTop: "0", fontSize: "14px", paddingBottom: "20px", fontFamily: "Inter", lineHeight: "25px", fontWeight: "400", color: "#808080" }}>
              {hasFacilityChanged && hasPayerChanged ? fAndPChanges : hasFacilityChanged ? facilityChangeOnly : hasPayerChanged ? payerChangeOnly : null}
            </Typography>

            {/* Instance where the New Payer from CRM doesn't exist in FinPass under the facility and Avg OON is selected */}
            {shouldShowUpdatedPayer &&
              <Typography className="pt-2" style={{ paddingTop: "0", fontSize: "12px", color: "red", paddingBottom: "14px", fontFamily: "Inter", lineHeight: "25px", fontWeight: "700", display: 'flex', alignItems: "start"}}>
                <WarningIcon style={{ fontSize: 12, marginTop: "6px", marginRight: "4px" }} />
                Updated Payer - {payerChange.ptRecordName}, isn’t available for Facility {facilityChange.newName}. Changing it will erase VOB details. Choose another or contact support. If none is selected, Average OON rates will apply.
              </Typography>
            }

            {/* Instance where the New Payer and Avg OON from CRM doesn't exist in FinPass under the facility */}            
            {noPayerExists &&
              <Typography className="pt-2" style={{ paddingTop: "0", fontSize: "12px", color: "red", paddingBottom: "14px", fontFamily: "Inter", lineHeight: "25px", fontWeight: "700", display: 'flex', alignItems: "start"}}>
                <WarningIcon style={{ fontSize: 12, marginTop: "6px", marginRight: "4px" }} />
                Updated Payer - {payerChange.ptRecordName}, isn’t available for Facility {facilityChange.newName}. Changing it will erase VOB details. Choose another or contact support.
              </Typography>
            }

            <Typography className="pt-2" style={{ paddingTop: "0", fontSize: "12px", color: "red", paddingBottom: "0px", fontFamily: "Inter", lineHeight: "25px", fontWeight: "700", display: 'flex', alignItems: "center"}}>
              <WarningIcon style={{ fontSize: 12, marginRight: "4px" }} />
                This will reset the VOB and Level of Care Estimate.
            </Typography>
          </Grid>
        </DialogContent>
        <DialogActions style={{ borderTop: "", marginTop: "0", padding: "2rem" }}>
          <Button onClick={() => {handleSave()}}>
              Save Changes
          </Button>
          <Button onClick={handleModalCancel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
  )
}